<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  avatarUrl: {
    type: String,
    default: '',
  },
  headerText: {
    type: String,
    default: '',
  },
  bodyParts: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: '',
  },
  footerText: {
    type: String,
    default: '',
  },
  buttons: {
    type: Array,
    default: () => [],
  },
  // The part showing a phrase that is only being pointed at, not applied yet.
  highlight: {
    type: String,
    default: '',
  },
});

// A click on a part asks to edit it: 'header', 'body', 'footer' or 'buttons'.
const emit = defineEmits(['select']);

const { t } = useI18n();

const partClass = part => [
  'cursor-pointer rounded -mx-1 px-1 transition-colors hover:bg-n-alpha-2',
  props.highlight === part
    ? 'outline outline-1 outline-dashed outline-n-teal-9'
    : '',
];

const BUTTON_ICONS = {
  QUICK_REPLY: 'i-woot-quick-reply',
  URL: 'i-lucide-external-link',
  PHONE_NUMBER: 'i-lucide-phone',
  COPY_CODE: 'i-lucide-copy',
};

const previewTime = new Date().toLocaleTimeString([], {
  hour: '2-digit',
  minute: '2-digit',
});

const hasBody = computed(() => props.bodyParts.some(part => part.value));
</script>

<template>
  <div
    class="overflow-hidden rounded-xl outline outline-1 outline-n-container -outline-offset-1 bg-n-solid-2"
  >
    <!-- Mini contact header -->
    <div
      class="flex items-center gap-2.5 px-3 py-2.5 border-b border-n-weak bg-n-solid-2"
    >
      <Avatar
        :name="name || t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW_BUSINESS_NAME')"
        :src="avatarUrl"
        rounded-full
        :size="32"
      />
      <div class="flex-1 min-w-0">
        <p class="m-0 text-sm font-semibold truncate text-n-slate-12">
          {{ name || t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW_BUSINESS_NAME') }}
        </p>
        <p class="m-0 text-xs truncate text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW_SUBTITLE') }}
        </p>
      </div>
    </div>

    <!-- Chat surface -->
    <div
      class="flex min-h-[24rem] flex-col justify-end gap-2 bg-n-solid-1 bg-[radial-gradient(rgba(148,163,184,0.12)_1px,transparent_0)] px-3 py-4 [background-size:16px_16px]"
    >
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        mode="out-in"
      >
        <div :key="buttons.length" class="max-w-[88%]">
          <div
            class="overflow-hidden rounded-xl rounded-tl-sm shadow-sm bg-n-slate-3 dark:bg-n-slate-4"
          >
            <div class="flex flex-col gap-1 px-3 py-2.5">
              <p
                v-if="headerText"
                class="m-0 text-sm font-bold leading-snug text-n-slate-12"
                :class="partClass('header')"
                @click="emit('select', 'header')"
              >
                {{ headerText }}
              </p>
              <p
                v-if="hasBody"
                class="m-0 text-sm leading-snug whitespace-pre-line text-n-slate-12"
                :class="partClass('body')"
                @click="emit('select', 'body')"
              >
                <template v-for="(part, index) in bodyParts" :key="index">
                  <span v-if="part.variable" class="font-medium text-n-teal-11">
                    {{ part.value }}
                  </span>
                  <template v-else>{{ part.value }}</template>
                </template>
              </p>
              <p
                v-else
                class="m-0 text-sm italic leading-snug text-n-slate-11"
                :class="partClass('body')"
                @click="emit('select', 'body')"
              >
                {{
                  placeholder ||
                  t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW_PLACEHOLDER_BODY')
                }}
              </p>
              <p
                v-if="footerText"
                class="m-0 text-xs leading-snug text-n-slate-11"
                :class="partClass('footer')"
                @click="emit('select', 'footer')"
              >
                {{ footerText }}
              </p>
              <span class="self-end text-[11px] text-n-slate-11">
                {{ previewTime }}
              </span>
            </div>

            <div
              v-for="(button, index) in buttons"
              :key="index"
              v-tooltip="button.link"
              class="flex items-center justify-center gap-1.5 px-3 py-2.5 cursor-pointer transition-colors hover:bg-n-alpha-2"
              :class="[
                index === 0
                  ? ''
                  : 'border-t border-n-slate-5 dark:border-n-slate-6',
                button.highlight
                  ? 'outline outline-1 outline-dashed outline-n-teal-9 -outline-offset-2'
                  : '',
              ]"
              @click="emit('select', 'buttons')"
            >
              <Icon
                :icon="BUTTON_ICONS[button.type] || BUTTON_ICONS.QUICK_REPLY"
                class="size-3.5 text-n-teal-11"
              />
              <span class="text-sm font-medium truncate text-n-teal-11">
                {{ button.text }}
              </span>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>
